import { StarIcon } from "@heroicons/react/20/solid";
import { InfoWithLabel } from "@/core/components/info-with-label";
import { InfoWithLabelSkeleton } from "@/core/components/info-with-label-skeleton";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { REVIEW_PLATFORM_TO_DISPLAY } from "@/activity-feed/constants";
import { cn } from "@/core/utils/utils";
import type { IReview } from "@/reviews/review";
import { ISpiff } from "@/spiffs/spiffs.d";
import FacebookLogoSrc from "@/assets/images/facebook-logo.png";
import GoogleLogoSrc from "@/assets/images/google-logo.png";
import TripAdvisorLogoSrc from "@/assets/images/trip-advisor-logo.jpeg";
import YelpLogoSrc from "@/assets/images/yelp-icon.png";

const PLATFORM_TO_ICON: Record<string, string> = {
  google: GoogleLogoSrc,
  facebook: FacebookLogoSrc,
  yelp: YelpLogoSrc,
  tripadvisor: TripAdvisorLogoSrc,
};

interface ReviewTriggerDetailsProps {
  spiff: ISpiff;
  isLoading?: boolean;
}

export const ReviewTriggerDetails = ({
  spiff,
  isLoading = false,
}: ReviewTriggerDetailsProps) => {
  const review = spiff.trigger as IReview;

  if (isLoading) {
    return (
      <Stack gap="sm" fullWidth>
        {/* Platform skeleton - icon + text */}
        <InfoWithLabelSkeleton contentWidth="w-24" />

        {/* Reviewer name skeleton */}
        <InfoWithLabelSkeleton contentWidth="w-40" />

        {/* Rating skeleton - 5 stars in a row */}
        <Stack gap="sm" fullWidth>
          <Skeleton className="h-5 w-16" />
          <Stack direction="row" gap="xs" align="center">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={`star-skeleton-${i}`} className="h-4 w-4" />
            ))}
          </Stack>
        </Stack>

        {/* Review body skeleton - multi-line text */}
        <Stack gap="sm" fullWidth>
          <Skeleton className="h-5 w-16" />
          <Stack gap="xs" fullWidth>
            <Skeleton className="h-[1.75rem] w-full" />
            <Skeleton className="h-[1.75rem] w-full" />
            <Skeleton className="h-[1.75rem] w-3/4" />
          </Stack>
        </Stack>
      </Stack>
    );
  }

  const platformIconSrc = review.platform
    ? PLATFORM_TO_ICON[review.platform]
    : undefined;

  // Ensure rating is a valid number between 0 and 5
  const rawRating = review.rating;
  const rating =
    typeof rawRating === "number" && !isNaN(rawRating)
      ? Math.max(0, Math.min(5, Math.round(rawRating)))
      : 0;

  return (
    <Stack gap="sm" fullWidth>
      <InfoWithLabel label="Platform">
        <Stack direction="row" align="center" gap="sm">
          {platformIconSrc && (
            <img
              src={platformIconSrc}
              alt={`${review.platform || "Google"} logo`}
              className="h-4 w-4 object-contain"
            />
          )}
          <Typography variant="bodyW1">
            {REVIEW_PLATFORM_TO_DISPLAY[
              review.platform as keyof typeof REVIEW_PLATFORM_TO_DISPLAY
            ] || "Google"}
          </Typography>
        </Stack>
      </InfoWithLabel>

      <InfoWithLabel label="Reviewer">{review.reviewer_name}</InfoWithLabel>

      <InfoWithLabel label="Rating">
        <Stack direction="row" gap="xs" align="center">
          {Array.from({ length: 5 }).map((_, i) => (
            <StarIcon
              key={`star-icon-${i}`}
              className={cn("h-4 w-4 text-gold-30", {
                "opacity-30": i >= rating,
              })}
            />
          ))}
        </Stack>
      </InfoWithLabel>

      {review.body && (
        <InfoWithLabel label="Review">
          <Typography variant="bodyW1" textColor="secondary">
            {review.body}
          </Typography>
        </InfoWithLabel>
      )}
    </Stack>
  );
};
